// ---- the field ----
// Who is standing where, the grown-up's place among them, somewhere new for a
// walker to go, and the collisions that keep them all out of each other.
//
// three.js draws; it does not collide. What a physics engine does for a
// character is done here by hand: nobody tests mesh against mesh. Each body is
// a handful of spheres set along its length, two bodies
// overlap when any two of their spheres do, and the deepest overlap pushes them
// apart along the ground. A walker that bumps into someone stops and thinks
// again rather than ploughing through. The grown-up is a capsule where they
// stand, so a trunk stays out of them too.
//
// What the games differ on is all in setField(): how big the field is, what
// shape the grown-up is, and what a body does when it is bumped — which for an
// animal is a brain state and for a bowl of tabbouleh is nothing at all.
import * as THREE from 'three';
import {T,cam,scene,view} from './scene';
import {ashore,crossesWater,onWater} from './water';
import {backOut,clamp,rnd} from './util';
import type {Body} from './types';

export const bodies:Body[]=[];
// The grown-up's place in the field: the group their body lives in — the game
// puts it there — and what the collisions need to know about them: how far
// their feet reach, whether they are walking, and whether they have just walked
// into someone, which is theirs to answer.
export const elder=new THREE.Group(); scene.add(elder);
export const elderBody={pad:0.6,walking:false,bumped:false};

export interface FieldCfg{
  bounds:{x:number;zFar:number;zNear:number};   // where anyone may go
  capsule:[number,number][];                    // [height, radius] up the grown-up's body
  spacing?:number;                              // rough spacing for choosing a spot
  cap?:number;                                  // how many bodies before the oldest leaves
  onBump?:(b:Body,t:number,nx:number,nz:number)=>void;   // somebody touched it, and which way it was pushed: a walker into them stops and thinks again; one brushing past walks on
}
let cfg:FieldCfg|null=null;
const C=():FieldCfg=>{if(!cfg)throw new Error('field: setField() has not been called');return cfg;};
// Note it does NOT touch elder.position: where the grown-up starts is the
// game's to say, and setField() resetting it to the origin silently undid the
// line above the call.
export function setField(c:FieldCfg){cfg=c;}
// Where anyone may go: x either side, z from the far edge to the near one. It is
// wider than the frame — the camera follows the grown-up about it rather than
// holding all of it from one spot — so whoever wanders to an edge may be off
// screen for a while, and comes back. The clamp in separate() is one unit outside it.
export const FIELD={get x(){return C().bounds.x},get zFar(){return C().bounds.zFar},get zNear(){return C().bounds.zNear}};
// Inside it, `inset` in from its edges — and the nearest point that is. Whoever
// is not a Body keeps to it themselves: separate() clamps only `bodies`, so for
// the people a spot outside it is a spot they stay at.
export const inBounds=(x:number,z:number,inset:number)=>{const B=C().bounds;return Math.abs(x)<=B.x-inset&&z>=B.zFar+inset&&z<=B.zNear-inset;};
export function intoBounds(p:{x:number;z:number},inset:number){
  const B=C().bounds; p.x=clamp(p.x,inset-B.x,B.x-inset); p.z=clamp(p.z,B.zFar+inset,B.zNear-inset); return p;
}
const clear=(x:number,z:number,pad:number)=>{const ER=C().spacing??1.5;return bodies.every(e=>Math.hypot(x-e.position.x,z-e.position.z)>2*ER+pad);};
// Somewhere new for a walker whose feet reach `pad`, standing at `from` (or
// arriving, with no from): on the grass, off the water and not across it, and
// out of everyone's way — that last one is the one to give up on when the field
// is crowded. `want` narrows it further (an arrival has to be in the frame).
// Nothing found in the draws means it stays where it is and thinks again after
// its pause; a spot on the water was never an answer.
export function fieldSpot(from:{x:number;z:number}|null,pad:number,want?:(x:number,z:number)=>boolean){
  const ER=C().spacing??1.5, B=C().bounds;
  let spare:{x:number;z:number}|null=null;
  for(let i=0;i<(want?48:24);i++){
    const x=rnd(-B.x,B.x), z=rnd(B.zFar,B.zNear);
    if(want&&!want(x,z))continue;
    if(onWater(x,z,pad)||(from&&crossesWater(from.x,from.z,x,z,pad)))continue;
    if(Math.hypot(x-elder.position.x,z-elder.position.z)>ER+0.9&&clear(x,z,0.3))return {x,z};
    spare=spare||{x,z};
  }
  if(spare)return spare;
  if(from)return {x:from.x,z:from.z};
  return {x:T.x-3*Math.sin(view.yaw),z:T.z-3*Math.cos(view.yaw)};   // an arrival's last resort: three units past them, in the frame
}
// In the frame, where a child can watch it arrive: inside the screen with a
// margin either side, and above the tray. The camera follows the grown-up, so
// this is the ground around them.
const v=new THREE.Vector3();
export const inFrame=(x:number,z:number)=>{v.set(x,0.6,z).project(cam);return Math.abs(v.x)<0.72&&v.y>-0.4&&v.y<0.85;};
// Somebody touched it. (nx,nz) is the way the shuffle pushed it, as a unit
// along the ground, so the game can tell a walker that walked INTO someone
// (pushed back against its heading) from one the shuffle merely slid past
// them. Without that, two bodies that come to rest touching cancel each
// other's every first step — the step overlaps by a hair, the push is a
// bump, a bump is a pause — and sit side by side for minutes; the soak
// (scripts/soak.mjs) read a walrus and a duck in the pond for thirteen.
export function bump(e:Body,t:number,nx=0,nz=0){e.bumped=true;C().onBump?.(e,t,nx,nz);}
// the deepest sphere overlap between two bodies, as a push along the ground
function overlap(a:Body,b:Body){
  let best=0, px=0, pz=0;
  for(const ca of a.cols){const ra=ca.r*a.scale.x;
    for(const cb of b.cols){const o=ra+cb.r*b.scale.x-ca.w.distanceTo(cb.w);
      if(o>best){best=o;px=cb.w.x-ca.w.x;pz=cb.w.z-ca.w.z;}}}
  if(best<=0)return null;
  let h=Math.hypot(px,pz);
  if(h<0.05){px=b.position.x-a.position.x;pz=b.position.z-a.position.z;h=Math.hypot(px,pz)||0.01;}
  return {o:best,nx:px/h,nz:pz/h};
}
export function separate(t:number){
  const JCOLS=C().capsule, B=C().bounds;
  // Three passes: pushing one pair apart can push a third one in, and a herd
  // converging on the pond is exactly when that happens.
  for(let pass=0;pass<3;pass++){
  for(const e of bodies)for(const c of e.cols)c.getWorldPosition(c.w);
  for(let i=0;i<bodies.length;i++){
    const a=bodies[i], p=a.position;
    for(let j=i+1;j<bodies.length;j++){
      const b=bodies[j], q=b.position;
      if(Math.hypot(q.x-p.x,q.z-p.z)>6)continue;                 // nowhere near
      const h=overlap(a,b); if(!h)continue;
      p.x-=h.nx*h.o*0.5; p.z-=h.nz*h.o*0.5; q.x+=h.nx*h.o*0.5; q.z+=h.nz*h.o*0.5;
      bump(a,t,-h.nx,-h.nz); bump(b,t,h.nx,h.nz);
    }
    let best=0, nx=0, nz=0;
    for(const c of a.cols)for(const [jy,jr] of JCOLS){
      const dx=c.w.x-elder.position.x, dz=c.w.z-elder.position.z;
      const o=c.r*a.scale.x+jr-Math.hypot(dx,c.w.y-jy,dz);
      if(o>best){best=o;const h=Math.hypot(dx,dz)||0.01;nx=dx/h;nz=dz/h;}
    }
    if(best>0){                                  // whoever was walking is the one who yields
      if(elderBody.walking){elder.position.x-=nx*best;elder.position.z-=nz*best;ashore(elder.position,elderBody.pad);elderBody.walking=false;elderBody.bumped=true;a.bumped=true;}
      else{p.x+=nx*best;p.z+=nz*best;bump(a,t,nx,nz);}
    }
    p.x=clamp(p.x,-B.x-1,B.x+1); p.z=clamp(p.z,B.zFar-1,B.zNear+0.6);
    if(!a.swims&&ashore(p,a.pad))bump(a,t);       // shoved onto the water: back to the bank, and think again
  }}
}
// a new body takes its place; past the cap the oldest one leaves
export function admit(b:Body,t:number){
  b.born=t; bodies.push(b);
  if(bodies.length>(C().cap??8))scene.remove(bodies.shift()!);   // nothing to dispose, it is all shared
  return b;
}
// an arrival grows in over its first 300ms
export const grow=(b:Body,t:number)=>b.scale.setScalar(b.full*Math.max(0.01,backOut(Math.min(1,(t-b.born)/300))));
